import { Store, MapPin } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransportLoadsBadge } from '~/components/ui/TransportLoadsBadge';
import {
  ContactCard,
  type ContactDetails,
} from '~/components/cards/ContactCard';
import { OrderSummary } from './OrderSummary';
import { CartListItem, type CartListItemHandle } from './CartListItem';
import { AdditionalItems } from './AdditionalItems';
import { CartTallyGroup } from './CartTallyGroup';
import { Link, useNavigate } from 'react-router';
import type {
  Cart as CartType,
  Facility,
  LogisticsIncoterm,
  Location,
  TransportVehicle,
} from '~/graphql/graphql';
import { isInternationalShippingComplete } from '~/utils/incoterms';
import {
  calcItemLoads,
  calcSupportedVehicleIds,
  getVehiclePayload,
  groupItemsByTally,
  type CartOrderItem,
  linesWithoutCapacityFor,
  productNamesOf,
} from '~/utils/order';
import {
  cannedTallyVehicleChoices,
  getPreferredOrFirstVehicle,
} from '~/utils/vehicleGroups';
import { facilityAddressCodeLabel } from '~/utils/facilityOptions';
import {
  calculateOrderTotal,
  extractGraphQLErrorMessages,
  type ClientError,
} from '~/utils';
import {
  deliveryMethodToIncoTerm,
  incoTermToDeliveryMethod,
  useAutoSelectTransportVehicle,
  useTransportState,
  vehicleTypeToTransportMode,
} from '~/hooks/useTransportState';
import {
  useCartTransportSave,
  useRetryTransportSaveWhen,
} from '~/hooks/useCartTransportSave';
import { useUpdateCartTarp } from '~/api';
import { useGetTransportVehicles } from '~/api/facility';
import toast from 'react-hot-toast';

interface CartCardProps {
  supplierName: string;
  organizationId: string;
  items?: CartOrderItem[];
  id: string;
  parentOrderId?: string | null;
  shippingLocation: Location;
  sellerOrganizationFacility: Facility;
  incoTerm?: LogisticsIncoterm | null;
  transportVehicle?: Pick<
    TransportVehicle,
    'id' | 'name' | 'vehicleType'
  > | null;
  isInternational?: boolean;
  isFromQuote?: boolean;
  incotermDischarge?: CartType['incotermDischarge'];
  incotermAddressDestination?: CartType['incotermAddressDestination'];
  deliveryAddress?: CartType['deliveryAddress'];
  deliveryMethod?: CartType['deliveryMethod'];
  portOfLoading?: CartType['portOfLoading'];
  portOfDischarge?: CartType['portOfDischarge'];
  shippingRoute?: CartType['shippingRoute'];
  tarp: boolean;
  currency?: CartType['currency'];
  contacts?: ContactDetails[];
}

function plainCartAutoSelectIds(
  fullIntersection: string[] | undefined,
  plainLineIds: string[] | undefined
) {
  if (fullIntersection && fullIntersection.length > 0) return fullIntersection;
  return plainLineIds ?? fullIntersection;
}

export function CartCard({
  supplierName,
  organizationId,
  items: itemsProp,
  id,
  parentOrderId,
  shippingLocation,
  sellerOrganizationFacility,
  incoTerm: savedIncoTerm,
  transportVehicle: savedTransportVehicle,
  isInternational = false,
  isFromQuote = false,
  incotermDischarge,
  incotermAddressDestination,
  deliveryAddress = null,
  deliveryMethod: internationalDeliveryMethod,
  portOfLoading,
  portOfDischarge,
  shippingRoute,
  tarp,
  currency: cartCurrency,
  contacts = [],
}: Readonly<CartCardProps>) {
  const { t } = useTranslation('order');
  const items = useMemo(() => itemsProp ?? [], [itemsProp]);
  const {
    failure: transportSaveFailure,
    save: saveTransport,
    retry: retryTransport,
    isSaving: isTransportSaving,
  } = useCartTransportSave(id);
  const transportSaveFailed = transportSaveFailure.kind !== 'none';
  const { mutate: updateCartTarp, isPending: isTarpSaving } =
    useUpdateCartTarp();
  const [currentTarp, setCurrentTarp] = useState(tarp);
  const [prevTarp, setPrevTarp] = useState(tarp);
  if (tarp !== prevTarp) {
    setPrevTarp(tarp);
    setCurrentTarp(tarp);
  }

  const handleTarpChange = (checked: boolean) => {
    setCurrentTarp(checked);
    updateCartTarp(
      { cartId: id, tarp: checked },
      {
        onError: (error) => {
          setCurrentTarp(!checked);
          toast.error(extractGraphQLErrorMessages(error).join(', '));
        },
      }
    );
  };
  const activeShippingFacility = shippingLocation.facility;
  const isSubOrder = !!parentOrderId;

  const {
    transport,
    deliveryMethod,
    selectedTransportVehicleId,
    handleTransportChange,
  } = useTransportState(saveTransport, {
    // International always behaves as delivered; the toggle is hidden in TransportCard.
    deliveryMethod: isInternational
      ? 'delivered'
      : incoTermToDeliveryMethod(savedIncoTerm),
    selectedTransportVehicleId: savedTransportVehicle?.id,
    transport: savedTransportVehicle
      ? vehicleTypeToTransportMode(savedTransportVehicle.vehicleType)
      : undefined,
  });

  const retryTransportSave = () => {
    if (!transportSaveFailed) return;
    retryTransport(deliveryMethod, selectedTransportVehicleId);
  };

  const totalLoads = items.reduce((acc, item) => {
    const vehiclePayload = getVehiclePayload(
      item.platformProductsLengths.platformProductVehicleCapacities,
      selectedTransportVehicleId
    );
    return acc + calcItemLoads(item, vehiclePayload);
  }, 0);

  const navigate = useNavigate();
  const itemHandleRefs = useRef(new Map<string, CartListItemHandle | null>());
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    try {
      // Any bundle-count edit still sitting in its per-item debounce must reach
      // the server before Checkout loads — it fetches the cart fresh, and
      // placeOrder acts on whatever quantity is currently persisted there.
      const saved = await Promise.all(
        Array.from(itemHandleRefs.current.values()).map((handle) =>
          handle?.flush()
        )
      );
      // A rejected save reports itself rather than throwing, so the guard has to
      // read the result: checkout re-fetches the cart and places whatever
      // quantity is persisted, which is not what the buyer just typed.
      if (saved.includes(false)) {
        setIsCheckingOut(false);
        return;
      }
    } catch (error) {
      toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
      setIsCheckingOut(false);
      return;
    }

    const incoTerm = deliveryMethodToIncoTerm(deliveryMethod);
    const params = new URLSearchParams({ incoTerm });
    if (selectedTransportVehicleId) {
      params.set('transportVehicleId', selectedTransportVehicleId);
    }
    navigate(
      `/cart/${id}/checkout/address/${shippingLocation.id}?${params.toString()}`
    );
  };

  const currentIncoTerm = deliveryMethodToIncoTerm(deliveryMethod);
  const subtotal = calculateOrderTotal({ items }, currentIncoTerm);
  const currency = cartCurrency ?? undefined;
  const itemCount = items.length;

  const supportedVehicleIds = calcSupportedVehicleIds(items);
  const failedVehicleNowHasCapacity =
    transportSaveFailure.kind === 'missing-capacity' &&
    linesWithoutCapacityFor(items, selectedTransportVehicleId).length === 0;
  useRetryTransportSaveWhen(failedVehicleNowHasCapacity, retryTransportSave);
  const { data: vehiclesData } = useGetTransportVehicles();

  const selectedVehicleName = vehiclesData?.transportVehicles?.find(
    (v) => v.id === selectedTransportVehicleId
  )?.name;

  // Auto-select would prefer DOMESTIC_TRUCK; force container on international carts
  // to avoid a flicker + backend clear when the product supports both vehicle types.
  const autoSelectCandidates = isInternational
    ? vehiclesData?.transportVehicles?.filter(
        (v) => v.vehicleType === 'INTERNATIONAL_CONTAINER'
      )
    : vehiclesData?.transportVehicles;

  const { tallyGroups, regularItems } = useMemo(
    () => groupItemsByTally(items),
    [items]
  );

  // Random-tally sellers aren't set up with the canned-tally-named vehicles.
  const hasCannedTallyItems = items.some(
    (item) => !!item.tallyPlatformProduct?.id && !!item.cannedTally
  );
  const plainLineVehicleIds = calcSupportedVehicleIds(regularItems);
  const { autoSelectIds: cannedAutoSelectIds, dropdownIds: cannedDropdownIds } =
    cannedTallyVehicleChoices(
      vehiclesData?.transportVehicles ?? [],
      plainLineVehicleIds,
      calcSupportedVehicleIds(items.filter((item) => !!item.cannedTally)),
      transport
    );
  const autoSelectSupportedIds = hasCannedTallyItems
    ? cannedAutoSelectIds
    : plainCartAutoSelectIds(supportedVehicleIds, plainLineVehicleIds);

  const firstAvailableVehicle = getPreferredOrFirstVehicle(
    autoSelectCandidates,
    autoSelectSupportedIds
  );
  // Empty carts have nothing to ship, so don't let the auto-select fall back to
  // the first facility vehicle and fire an unnecessary updateCartTransport.
  const availableVehicles =
    itemCount > 0 && firstAvailableVehicle
      ? [firstAvailableVehicle]
      : undefined;

  useAutoSelectTransportVehicle(
    availableVehicles,
    selectedTransportVehicleId,
    handleTransportChange,
    deliveryMethod
  );

  const internationalSelectionsComplete =
    !isInternational ||
    isInternationalShippingComplete({
      deliveryMethod: internationalDeliveryMethod,
      incotermDischarge,
      incotermAddressDestination,
      portOfLoading,
      portOfDischarge,
      shippingRoute,
    });

  // The seller facility's default vehicle is domestic even for international sellers.
  // Treat it as "no vehicle selected" until the buyer picks a container in the sidebar.
  // Independent of transportSaveFailed: a missing-capacity save failure must still show
  // displayTransport, since the item list's "add capacity" affordance is how it self-heals.
  const hasUsableTransportVehicle =
    !!selectedTransportVehicleId &&
    (!isInternational || transport === 'container');

  const displayTransport = hasUsableTransportVehicle ? transport : undefined;

  const hasUnresolvedBundleSize = items.some(
    (item) => item.effectivePiecesPerBundle <= 0
  );

  const linesNeedingCapacity = hasUsableTransportVehicle
    ? linesWithoutCapacityFor(items, selectedTransportVehicleId)
    : [];
  const productsNeedingCapacity = productNamesOf(linesNeedingCapacity);

  const isCheckoutDisabled =
    !hasUsableTransportVehicle ||
    linesNeedingCapacity.length > 0 ||
    !internationalSelectionsComplete ||
    hasUnresolvedBundleSize ||
    transportSaveFailed;

  // Canned-tally sellers ship only on the named vehicles; keep the saved vehicle listed so the select can show it.
  const effectiveSupportedVehicleIds = hasCannedTallyItems
    ? (vehiclesData?.transportVehicles ?? [])
        .filter(
          (v) =>
            v.id === selectedTransportVehicleId ||
            cannedDropdownIds.includes(v.id)
        )
        .map((v) => v.id)
    : undefined;

  return (
    <div className="bg-neut-bg rounded-lg border border-neut-200 shadow-sm overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-[400px]">
        <div className="lg:col-span-2 flex flex-col">
          <div className="p-4 sm:p-6 border-b border-neut-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-success-400 to-prim-500 rounded-lg flex items-center justify-center shrink-0">
                <Store className="w-5 h-5 text-neut-0" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-neut-prim font-semibold">
                    {supplierName}
                  </h3>
                  {parentOrderId && (
                    <Link
                      to={`/orders/view/${parentOrderId}`}
                      onClick={(event) => event.stopPropagation()}
                      className="bg-indigo-50 text-indigo-600 rounded-full px-2 py-1 text-xs font-medium hover:bg-indigo-100 transition-colors"
                    >
                      {t('order.child_of_parent', {
                        parentOrderId: parentOrderId.slice(0, 8),
                      })}
                    </Link>
                  )}
                </div>
                {sellerOrganizationFacility && (
                  <div className="flex items-start gap-1 text-xs text-neut-sec mt-0.5">
                    <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                    <div>
                      <span>
                        {facilityAddressCodeLabel(sellerOrganizationFacility)}
                      </span>
                    </div>
                  </div>
                )}
                {contacts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {contacts.map((contact) => (
                      <div key={contact.id} className="w-64 min-w-0">
                        <ContactCard contact={contact} compact />
                      </div>
                    ))}
                  </div>
                )}
                {hasUsableTransportVehicle &&
                  !transportSaveFailed &&
                  !isTransportSaving && (
                    <div className="mt-1.5">
                      <TransportLoadsBadge
                        transport={transport}
                        loads={totalLoads}
                        vehicleName={selectedVehicleName}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0" data-testid="cart-items">
            {Array.from(tallyGroups.entries()).map(
              ([groupKey, { tallyId, isRandomTally, items: tallyItems }]) => (
                <CartTallyGroup
                  key={groupKey}
                  items={tallyItems}
                  tallyPlatformProductId={tallyId}
                  isRandomTally={isRandomTally}
                  organizationId={organizationId}
                  cartId={id}
                  locationId={shippingLocation.id}
                  selectedTransportVehicleId={selectedTransportVehicleId}
                  sellingOrganizationFacilityId={sellerOrganizationFacility.id}
                  transport={transport}
                  parentOrderId={parentOrderId}
                  isFromQuote={isFromQuote}
                  currency={cartCurrency}
                  incoTerm={currentIncoTerm}
                />
              )
            )}
            {regularItems.map((item) => {
              const itemId = item?.platformProductsLengths?.id;
              return (
                <CartListItem
                  key={itemId}
                  ref={(handle) => {
                    if (!itemId) return;
                    itemHandleRefs.current.set(itemId, handle);
                    return () => {
                      itemHandleRefs.current.delete(itemId);
                    };
                  }}
                  item={item}
                  cartId={id}
                  locationId={shippingLocation.id}
                  transport={displayTransport}
                  totalLoads={totalLoads}
                  selectedTransportVehicleId={selectedTransportVehicleId}
                  sellingOrganizationFacilityId={sellerOrganizationFacility.id}
                  parentOrderId={parentOrderId}
                  isFromQuote={isFromQuote}
                  currency={cartCurrency}
                  incoTerm={currentIncoTerm}
                />
              );
            })}
          </div>
        </div>

        <OrderSummary
          shippingLocation={shippingLocation}
          deliveryAddress={deliveryAddress}
          subtotal={subtotal}
          currency={currency}
          itemCount={itemCount}
          onCheckout={handleCheckout}
          isPlaceOrderDisabled={isCheckoutDisabled}
          isCheckingOut={isCheckingOut || isTarpSaving}
          sellerOrganizationFacility={sellerOrganizationFacility}
          shippingLocationFacility={activeShippingFacility}
          deliveryMethod={deliveryMethod}
          selectedTransportVehicleId={selectedTransportVehicleId}
          supportedVehicleIds={effectiveSupportedVehicleIds}
          transportVehicleCatalog={vehiclesData?.transportVehicles}
          onTransportChange={handleTransportChange}
          cartId={id}
          isInternational={isInternational}
          isSubOrder={isSubOrder}
          hasUsableTransportVehicle={hasUsableTransportVehicle}
          transportSaveFailure={transportSaveFailure}
          onRetryTransportSave={retryTransportSave}
          productsNeedingCapacity={productsNeedingCapacity}
          isTransportSaving={isTransportSaving}
          hasUnresolvedBundleSize={hasUnresolvedBundleSize}
          tarp={currentTarp}
          onTarpChange={handleTarpChange}
          international={
            isInternational
              ? {
                  incoTerm: savedIncoTerm,
                  incotermDischarge,
                  incotermAddressDestination,
                  deliveryMethod: internationalDeliveryMethod,
                  portOfLoading,
                  portOfDischarge,
                  shippingRoute,
                }
              : undefined
          }
        />

        {!isFromQuote && (
          <AdditionalItems
            cartId={id}
            cartItems={items}
            locationId={shippingLocation.id}
            locationName={shippingLocation.name}
            sellerOrganizationId={organizationId}
            sellerOrganizationFacilityId={sellerOrganizationFacility.id}
            transport={displayTransport}
            totalLoads={totalLoads}
            selectedTransportVehicleId={selectedTransportVehicleId}
            parentOrderId={parentOrderId}
            incoTerm={savedIncoTerm}
            cartCurrency={cartCurrency}
            hasShippingRoute={!!shippingRoute}
          />
        )}
      </div>
    </div>
  );
}
